import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Layers,
  CreditCard,
} from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { formatApiError } from '@/lib/format-api-error';
import type { CardGroup, StoryCard, Persona, PaginatedResponse } from '@/types';

// ==================== Card Groups Tab ====================

function CardGroupsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<CardGroup | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    color: '',
    isActive: true,
    sortOrder: 0,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'card-groups', page, search],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        limit: '20',
      };
      if (search) params.search = search;

      const response = await apiClient.get<PaginatedResponse<CardGroup>>(
        '/api/v1/admin/story-cards/groups',
        params
      );
      if (!response.success) throw new Error(response.error?.message);

      return {
        items: (response.data as unknown as CardGroup[]) || [],
        meta: response.meta || { page: 1, limit: 20, total: 0, totalPages: 0 },
      } as PaginatedResponse<CardGroup>;
    },
  });

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      icon: '',
      color: '',
      isActive: true,
      sortOrder: 0,
    });
    setIsEditing(false);
    setSelectedGroup(null);
    setSaveError(null);
    setFormOpen(true);
  };

  const handleEdit = (group: CardGroup) => {
    setFormData({
      name: group.name,
      description: group.description || '',
      icon: group.icon || '',
      color: group.color || '',
      isActive: group.isActive,
      sortOrder: group.sortOrder,
    });
    setIsEditing(true);
    setSelectedGroup(group);
    setSaveError(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    const payload = {
      ...formData,
      description: formData.description || undefined,
      icon: formData.icon || undefined,
      color: formData.color || undefined,
    };
    const response =
      isEditing && selectedGroup
        ? await apiClient.put(`/api/v1/admin/story-cards/groups/${selectedGroup.id}`, payload)
        : await apiClient.post('/api/v1/admin/story-cards/groups', payload);
    setIsSaving(false);
    if (response.success) {
      setFormOpen(false);
      refetch();
    } else {
      setSaveError(formatApiError(response.error));
    }
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;
    setIsDeleting(true);
    try {
      const response = await apiClient.delete(
        `/api/v1/admin/story-cards/groups/${selectedGroup.id}`
      );
      if (response.success) {
        setDeleteOpen(false);
        refetch();
      }
    } catch {
      // handled
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search groups..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Group
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          No card groups found
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((group) => (
                  <TableRow key={group.id} className={!group.isActive ? 'opacity-60' : undefined}>
                    <TableCell className="font-medium">{group.name}</TableCell>
                    <TableCell className="max-w-[300px] truncate text-muted-foreground">
                      {group.description || '—'}
                    </TableCell>
                    <TableCell>
                      {group.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleEdit(group)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedGroup(group);
                              setDeleteOpen(true);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {data.meta.totalPages > 1 && (
            <div className="flex items-center justify-between px-2 py-4">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * data.meta.limit + 1} to{' '}
                {Math.min(page * data.meta.limit, data.meta.total)} of {data.meta.total} groups
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {data.meta.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.meta.totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {isEditing ? 'Edit Card Group' : 'Create Card Group'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the card group details'
                : 'Create a new card group to organize story cards'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="groupName">Name</Label>
              <Input
                id="groupName"
                placeholder="e.g. Childhood"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="groupDesc">Description</Label>
              <Textarea
                id="groupDesc"
                placeholder="Optional description..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="groupIcon">Icon</Label>
                <Input
                  id="groupIcon"
                  placeholder="e.g. fa-solid fa-children"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="groupColor">Color</Label>
                <Input
                  id="groupColor"
                  placeholder="e.g. #C9A84C"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="groupActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="groupActive">Active</Label>
            </div>
          </div>
          {saveError && <p className="whitespace-pre-line text-sm text-destructive">{saveError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !formData.name}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Card Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedGroup?.name}"? This will also delete all
              story cards in this group. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== Story Cards Tab ====================

function StoryCardsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [depthFilter, setDepthFilter] = useState<string>('all');

  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<StoryCard | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    cardId: '',
    depth: 1,
    title: '',
    prompt: '',
    narrativeLens: '',
    showWhen: '',
    icon: '',
    color: '',
    examplesText: '',
    groupId: '',
    parentCardId: '' as string | null,
    isActive: true,
    sortOrder: 0,
    personaIds: [] as string[],
  });

  // Fetch card groups for filter dropdown
  const { data: groupsData } = useQuery({
    queryKey: ['admin', 'card-groups', 'all'],
    queryFn: async () => {
      const response = await apiClient.get<CardGroup[]>('/api/v1/admin/story-cards/groups/all');
      if (!response.success) throw new Error(response.error?.message);
      return (response.data as unknown as CardGroup[]) || [];
    },
  });

  // Fetch all personas for multi-select
  const { data: personasData } = useQuery({
    queryKey: ['admin', 'personas', 'all'],
    queryFn: async () => {
      const response = await apiClient.get<Persona[]>('/api/v1/admin/personas/all');
      if (!response.success) throw new Error(response.error?.message);
      return (response.data as unknown as Persona[]) || [];
    },
  });

  // Fetch story cards
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'story-cards', page, search, groupFilter, depthFilter],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        limit: '15',
      };
      if (search) params.search = search;
      if (groupFilter !== 'all') params.groupId = groupFilter;
      if (depthFilter !== 'all') params.depth = depthFilter;

      const response = await apiClient.get<PaginatedResponse<StoryCard>>(
        '/api/v1/admin/story-cards/cards',
        params
      );
      if (!response.success) throw new Error(response.error?.message);

      return {
        items: (response.data as unknown as StoryCard[]) || [],
        meta: response.meta || { page: 1, limit: 15, total: 0, totalPages: 0 },
      } as PaginatedResponse<StoryCard>;
    },
  });

  const handleCreate = () => {
    setFormData({
      cardId: '',
      depth: 1,
      title: '',
      prompt: '',
      narrativeLens: '',
      showWhen: '',
      icon: '',
      color: '',
      examplesText: '',
      groupId: '',
      parentCardId: null,
      isActive: true,
      sortOrder: 0,
      personaIds: personasData?.map((p) => p.id) || [],
    });
    setIsEditing(false);
    setSelectedCard(null);
    setSaveError(null);
    setFormOpen(true);
  };

  const handleEdit = async (card: StoryCard) => {
    // Fetch full card details with persona assignments
    const response = await apiClient.get<StoryCard>(`/api/v1/admin/story-cards/cards/${card.id}`);
    if (!response.success) return;

    const detail = response.data as unknown as {
      personas?: { personaId: string }[];
    } & StoryCard;

    setFormData({
      cardId: card.cardId,
      depth: card.depth,
      title: card.title,
      prompt: card.prompt || '',
      narrativeLens: card.narrativeLens || '',
      showWhen: card.showWhen || '',
      icon: card.icon || '',
      color: card.color || '',
      examplesText: (card.examples ?? []).join('\n'),
      groupId: card.groupId,
      parentCardId: card.parentCardId,
      isActive: card.isActive,
      sortOrder: card.sortOrder,
      personaIds: detail.personas?.map((p) => p.personaId) || [],
    });
    setIsEditing(true);
    setSelectedCard(card);
    setSaveError(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    const { examplesText, ...rest } = formData;
    const examples = examplesText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const payload = {
      ...rest,
      prompt: formData.prompt || undefined,
      narrativeLens: formData.narrativeLens || undefined,
      showWhen: formData.showWhen || undefined,
      icon: formData.icon || undefined,
      color: formData.color || undefined,
      examples,
      parentCardId: formData.parentCardId || null,
    };
    const response =
      isEditing && selectedCard
        ? await apiClient.put(`/api/v1/admin/story-cards/cards/${selectedCard.id}`, payload)
        : await apiClient.post('/api/v1/admin/story-cards/cards', payload);
    setIsSaving(false);
    if (response.success) {
      setFormOpen(false);
      refetch();
    } else {
      setSaveError(formatApiError(response.error));
    }
  };

  const handleDelete = async () => {
    if (!selectedCard) return;
    setIsDeleting(true);
    try {
      const response = await apiClient.delete(`/api/v1/admin/story-cards/cards/${selectedCard.id}`);
      if (response.success) {
        setDeleteOpen(false);
        refetch();
      }
    } catch {
      // handled
    } finally {
      setIsDeleting(false);
    }
  };

  const togglePersona = (personaId: string) => {
    setFormData((prev) => ({
      ...prev,
      personaIds: prev.personaIds.includes(personaId)
        ? prev.personaIds.filter((id) => id !== personaId)
        : [...prev.personaIds, personaId],
    }));
  };

  const clearFilters = () => {
    setSearch('');
    setGroupFilter('all');
    setDepthFilter('all');
    setPage(1);
  };

  const hasFilters = search || groupFilter !== 'all' || depthFilter !== 'all';

  const depthLabel = (depth: number) => {
    switch (depth) {
      case 1:
        return 'Group Root';
      case 2:
        return 'Child';
      case 3:
        return 'Grandchild';
      default:
        return `Depth ${depth}`;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by title, ID, or prompt..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={groupFilter}
          onValueChange={(v) => {
            setGroupFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Card Group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            {groupsData?.map((group) => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={depthFilter}
          onValueChange={(v) => {
            setDepthFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Depth" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Depths</SelectItem>
            <SelectItem value="1">Depth 1</SelectItem>
            <SelectItem value="2">Depth 2</SelectItem>
            <SelectItem value="3">Depth 3</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            Clear
          </Button>
        )}
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Card
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          No story cards found
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead className="w-[90px]">Depth</TableHead>
                  <TableHead>Personas</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((card) => (
                  <TableRow key={card.id} className={!card.isActive ? 'opacity-60' : undefined}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {card.cardId}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{card.title}</p>
                        {card.prompt && (
                          <p className="max-w-[300px] truncate text-xs text-muted-foreground">
                            {card.prompt}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{card.groupName || '—'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          card.depth === 1 ? 'default' : card.depth === 2 ? 'secondary' : 'outline'
                        }
                      >
                        {depthLabel(card.depth)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {card.personaCodes && card.personaCodes.length > 0 ? (
                          card.personaCodes.length <= 4 ? (
                            card.personaCodes.map((code) => (
                              <Badge key={code} variant="outline" className="text-xs">
                                {code}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              {card.personaCodes.length} personas
                            </Badge>
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {card.isActive ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedCard(card);
                              setDetailOpen(true);
                            }}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(card)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedCard(card);
                              setDeleteOpen(true);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-2 py-4">
            <p className="text-sm text-muted-foreground">
              Showing {(page - 1) * data.meta.limit + 1} to{' '}
              {Math.min(page * data.meta.limit, data.meta.total)} of {data.meta.total} cards
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {data.meta.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= data.meta.totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {selectedCard?.title}
              <Badge variant="outline" className="ml-2 font-mono">
                {selectedCard?.cardId}
              </Badge>
            </DialogTitle>
            <DialogDescription>Story card details</DialogDescription>
          </DialogHeader>
          {selectedCard && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-foreground">Group</Label>
                  <p className="mt-1 text-sm font-normal text-muted-foreground">
                    {selectedCard.groupName || '—'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-foreground">Depth</Label>
                  <p className="mt-1 text-sm font-normal text-muted-foreground">
                    {depthLabel(selectedCard.depth)}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-foreground">Narrative Lens</Label>
                  <p className="mt-1 text-sm font-normal text-muted-foreground">
                    {selectedCard.narrativeLens || '—'}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-foreground">Prompt</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm font-normal text-muted-foreground">
                  {selectedCard.prompt || 'Not specified'}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-foreground">Icon</Label>
                  <p className="mt-1 flex items-center gap-2 text-sm font-normal text-muted-foreground">
                    {selectedCard.icon ? (
                      <>
                        <i className={selectedCard.icon} aria-hidden="true" />
                        <span className="font-mono">{selectedCard.icon}</span>
                      </>
                    ) : (
                      '—'
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-foreground">Color</Label>
                  <p className="mt-1 flex items-center gap-2 text-sm font-normal text-muted-foreground">
                    {selectedCard.color ? (
                      <>
                        <span
                          className="inline-block h-4 w-4 rounded border border-input"
                          style={{ backgroundColor: selectedCard.color }}
                          aria-hidden="true"
                        />
                        <span className="font-mono">{selectedCard.color}</span>
                      </>
                    ) : (
                      '—'
                    )}
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-foreground">Persona Visibility</Label>
                <div className="mt-2 flex flex-wrap gap-2 rounded-md border p-3">
                  {personasData && personasData.length > 0 ? (
                    personasData.map((persona) => {
                      const isAssigned = selectedCard.personaCodes?.includes(persona.code);
                      return (
                        <span
                          key={persona.id}
                          className={`inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium ${
                            isAssigned
                              ? 'bg-primary text-primary-foreground'
                              : 'border border-input bg-background text-muted-foreground opacity-40'
                          }`}
                        >
                          {persona.code} - {persona.name}
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-sm text-muted-foreground">None assigned</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {isEditing ? 'Edit Story Card' : 'Create Story Card'}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update the story card details' : 'Create a new story card'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scCardId">Card ID</Label>
                <Input
                  id="scCardId"
                  placeholder="e.g. C101"
                  value={formData.cardId}
                  onChange={(e) => setFormData({ ...formData, cardId: e.target.value })}
                  disabled={isEditing}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scDepth">Depth</Label>
                <Select
                  value={String(formData.depth)}
                  onValueChange={(v) => setFormData({ ...formData, depth: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Group Root</SelectItem>
                    <SelectItem value="2">2 - Child</SelectItem>
                    <SelectItem value="3">3 - Grandchild</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="scGroup">Card Group</Label>
                <Select
                  value={formData.groupId}
                  onValueChange={(v) => setFormData({ ...formData, groupId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupsData?.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="scTitle">Title</Label>
              <Input
                id="scTitle"
                placeholder="Card title..."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scPrompt">Prompt</Label>
              <Textarea
                id="scPrompt"
                placeholder="The question or prompt shown to the user..."
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scExamples">Examples</Label>
              <Textarea
                id="scExamples"
                placeholder={
                  'One example per line — shown as bullets on the card.\nLeave blank to show the default placeholder dots.'
                }
                value={formData.examplesText}
                onChange={(e) => setFormData({ ...formData, examplesText: e.target.value })}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Enter one example per line. Blank lines are ignored.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="scLens">Narrative Lens</Label>
              <Input
                id="scLens"
                placeholder="e.g. Life Story Cards"
                value={formData.narrativeLens}
                onChange={(e) => setFormData({ ...formData, narrativeLens: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scIcon">Icon</Label>
                <Input
                  id="scIcon"
                  placeholder="e.g. fa-solid fa-children"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scColor">Color</Label>
                <Input
                  id="scColor"
                  placeholder="e.g. #C9A84C"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="scActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="scActive">Active</Label>
            </div>

            {/* Persona assignments */}
            <div className="space-y-2">
              <Label>Persona Visibility</Label>
              <p className="text-xs text-muted-foreground">
                Select which personas can see this card
              </p>
              <div className="flex flex-wrap gap-2 rounded-md border p-3">
                {personasData?.map((persona) => (
                  <Button
                    key={persona.id}
                    type="button"
                    variant={formData.personaIds.includes(persona.id) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => togglePersona(persona.id)}
                  >
                    {persona.code} - {persona.name}
                  </Button>
                ))}
                {(!personasData || personasData.length === 0) && (
                  <span className="text-sm text-muted-foreground">
                    No personas available. Create personas first.
                  </span>
                )}
              </div>
            </div>
          </div>
          {saveError && <p className="whitespace-pre-line text-sm text-destructive">{saveError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !formData.cardId || !formData.title || !formData.groupId}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Card'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Story Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCard?.title}" ({selectedCard?.cardId})? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== Main Page ====================

export function StoryCardsPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-foreground">Story Cards</h1>
        <p className="text-muted-foreground">
          Manage card groups and story cards that guide user interviews
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="cards" className="space-y-4">
        <TabsList>
          <TabsTrigger value="groups" className="gap-2">
            <Layers className="h-4 w-4" />
            Card Groups
          </TabsTrigger>
          <TabsTrigger value="cards" className="gap-2">
            <CreditCard className="h-4 w-4" />
            Story Cards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="groups">
          <Card>
            <CardContent className="pt-6">
              <CardGroupsTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cards">
          <Card>
            <CardContent className="pt-6">
              <StoryCardsTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
